import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMapGL, { MapRef, ViewState } from "react-map-gl";
import { useTranslation } from "react-i18next";
import styles from "../../main/ObjectDetails/ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import Details from "./ActiveTabs/Details";
import Contacts from "./ActiveTabs/Contacts";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { ICurrentMarker } from "../../../../models/CurrentMarker";
import { CurrentPerson } from "../../../../models/CurrentPerson";
import {
  defaultViewport,
  mapboxAccessToken,
} from "../../../../constants/mapConstants";
import util from "../../../../util";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import Np from "../../../../assets/svg/personIcon.svg";

interface ListViewPersonDetailsProps {
  person: CurrentPerson;
  isDetailsActive: boolean;
  onClose: () => void;
}

const PERSON_MAP_STYLE = "mapbox://styles/aimap/cld0btpqm004t14p2gne4uyfz";

const ListViewPersonDetails: React.FC<ListViewPersonDetailsProps> = ({
  person,
  isDetailsActive,
  onClose,
}) => {
  const selectedLanguage = useSelectedLanguage();
  const { t: tObjectDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails",
  });

  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>(() => {
    const initialLat = Number(person?.lon);
    const initialLon = Number(person?.lat);
    const hasInitialCoordinates =
      Number.isFinite(initialLat) && Number.isFinite(initialLon);

    return {
      latitude: hasInitialCoordinates ? initialLat : defaultViewport.latitude,
      longitude: hasInitialCoordinates ? initialLon : defaultViewport.longitude,
      zoom: hasInitialCoordinates ? 13 : defaultViewport.zoom,
      bearing: 0,
      pitch: 0,
    };
  });

  useEffect(() => {
    const rawLat = Number(person?.lon);
    const rawLon = Number(person?.lat);
    const hasCoordinates = Number.isFinite(rawLat) && Number.isFinite(rawLon);

    setViewState((prev) => ({
      ...prev,
      latitude: hasCoordinates ? rawLat : defaultViewport.latitude,
      longitude: hasCoordinates ? rawLon : defaultViewport.longitude,
      zoom: hasCoordinates ? 13 : defaultViewport.zoom,
    }));
  }, [person?.lat, person?.lon]);

  const marker = useMemo<ICurrentMarker>(() => {
    const latitude = Number(person?.lon);
    const longitude = Number(person?.lat);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { lat: latitude, lon: longitude };
    }

    return null;
  }, [person?.lat, person?.lon]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (map) {
      util.mapInterfaceUtil.changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const description = person?.ADDRESS || "";
  const title = person?.NAME || "";

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={<img src={Np} alt="" />}
      />
      <div className={styles.listViewDetailsLayout}>
        <div className={styles.listViewMapWrapper}>
          <ReactMapGL
            {...viewState}
            ref={(instance) => (mapRef.current = instance)}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={PERSON_MAP_STYLE}
            onMove={(event) => setViewState(event.viewState)}
            dragRotate={false}
            doubleClickZoom={false}
            scrollZoom={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
            onLoad={handleMapLoad}
          >
            {marker && <CustomMarker marker={marker} />}
          </ReactMapGL>
        </div>
        <div className={styles.listViewPanels}>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("details.title")}
            </h3>
            <Details
              key={`details-${person?.RECORD ?? person?.NAME}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("contacts.title")}
            </h3>
            <Contacts
              key={`contacts-${person?.RECORD ?? person?.NAME}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
        </div>
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewPersonDetails;
